import React from 'react';
import SwapInterfaceV2 from './components/SwapInterfaceV2';
import { useSwapperV2 } from './hooks/useSwapperV2';
import { TokenLogos } from './assets/tokenLogos';

function V2Page({ account, onConnect }) {
  const { executeSwap, isSwapping } = useSwapperV2();

  const handleSwap = async (swapData) => {
    try {
      await executeSwap(swapData);
    } catch (error) {
      console.error('Swap failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 p-4">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <header className="flex items-center justify-between mb-8 pt-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden">
              <img 
                src={TokenLogos.GIGACHAD} 
                alt="Chad Swap" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Chad Swap</h1>
              <p className="text-sm text-white/60">Multi-DEX Aggregator V2</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Version Badge */}
            <div className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg">
              <span className="text-white font-semibold text-sm">V2 BETA</span>
            </div>

            {/* Link to Fountain */}
            <a
              href="/fountain"
              className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-400/50 text-green-300 rounded-lg transition-colors text-sm font-medium"
            >
              ⛲ Fountain
            </a>

            {/* Link to V1 */}
            <a
              href="/v1"
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm font-medium"
            >
              ← V1
            </a>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Swap Interface */}
          <div className="flex-1 w-full">
            <SwapInterfaceV2
              account={account}
              onConnect={onConnect}
              onSwap={handleSwap}
              isSwapping={isSwapping}
            />
          </div>

          {/* Info Panel */}
          <div className="w-full lg:w-96 space-y-4">
            {/* Features */}
            <div className="p-6 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">✨ New Features</h3>
              <ul className="space-y-3 text-white/80">
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Multi-DEX routing across Lif3, SpookySwap & WigoSwap</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Support for both V2 and V3 liquidity pools</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Custom token support - add any ERC20 token</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Automatic best price discovery</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Same 1% fee structure</span>
                </li>
              </ul>
            </div>

            {/* Supported DEXes */}
            <div className="p-6 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">🔄 Supported DEXes</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-white font-medium">Lif3 V3</span>
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded">V3</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-white font-medium">Lif3 V2</span>
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded">V2</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-white font-medium">SpookySwap V3</span>
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded">V3</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-white font-medium">SpookySwap V2</span>
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded">V2</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-white font-medium">WigoSwap V2</span>
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded">V2</span>
                </div>
              </div>
            </div>

            {/* Whitelisted Tokens */}
            <div className="p-6 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">🪙 Whitelisted Tokens</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-white/80">
                  <img src={TokenLogos.FTM} alt="FTM" className="w-5 h-5 rounded-full" />
                  <span>FTM & WFTM</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <img src={TokenLogos.LIF3_NEW} alt="LIF3" className="w-5 h-5 rounded-full" />
                  <span>LIF3 (New & Old)</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <img src={TokenLogos.LSHARE_NEW} alt="LSHARE" className="w-5 h-5 rounded-full" />
                  <span>LSHARE (New & Old)</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <img src={TokenLogos.L3USD} alt="L3USD" className="w-5 h-5 rounded-full" />
                  <span>L3USD</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <img src={TokenLogos.AXLUSDC} alt="axlUSDC" className="w-5 h-5 rounded-full" />
                  <span>axlUSDC</span>
                </div>
                <div className="mt-3 pt-3 border-t border-white/10 text-white/60 text-xs">
                  + Add custom tokens by address
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
              <div className="flex items-start gap-2">
                <span className="text-2xl">⚠️</span>
                <div className="text-sm text-yellow-300">
                  <p className="font-semibold mb-1">Beta Version</p>
                  <p className="text-yellow-300/80">
                    This is a beta version. Please test with small amounts first. 
                    V1 remains available and stable.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 pb-8 text-center text-white/40 text-sm">
          <p>ChadSwap V2 - Multi-DEX Aggregator on Fantom</p>
          <p className="mt-1">Fee: 1% per swap</p>
          <p className="mt-2 flex items-center justify-center gap-3">
            <a href="/v1" className="text-blue-400 hover:text-blue-300 underline">
              Switch to V1 (LIF3/LSHARE only)
            </a>
            <span>|</span>
            <a href="/fountain" className="text-blue-400 hover:text-blue-300 underline">
              Fountain (Staking)
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}

export default V2Page;
